import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/auth';
import { toast } from 'sonner';

/**
 * usePlanningBoard — owns the planning board data & derived indices.
 *
 * Inputs:
 *  - year, week        : the ISO year/week to load
 *  - filterFirmIds     : Set<firmId> — multi-select employer firm filter
 *                        (board cell visibility); empty = all firms
 *  - filterCompanyIds  : Set<companyId> — customer filter (rows shown)
 *  - filterDepotIds    : Set<depotId>   — depot-level subfilter
 *  - filterRouteIds    : Set<routeId>   — route-level subfilter
 *
 * Returns:
 *  - board, employees, loading
 *  - routeGroups       : nested company → depots → routes for rendering
 *  - assignByCell      : Map "routeId|date" → assignment (firm-filter aware)
 *  - leaveByEmpDate    : Set "userId|date" of approved-leave blocks
 *  - employeeBookedDates: Map empId → Set(date) of any booking
 *  - employeeWeekSummary: Map empId → ordered [{date, type, route_code, route_id, ...}]
 *  - missingCount, conflictCount
 *  - fetchBoard()      : explicit refetch
 */
export function usePlanningBoard({ year, week, filterFirmIds, filterCompanyIds, filterDepotIds, filterRouteIds, lockedDates, showSaturday }) {
  const [board, setBoard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch ALL employees so we can do client-side firm filtering
      // (incl. cross-firm "Ödünç" borrow). The board firm filter only affects
      // the cell rendering, not the available-to-drag employee pool.
      const [boardRes, empRes] = await Promise.all([
        api.get(`/planning/board?year=${year}&week=${week}`),
        api.get('/planning/employees'),
      ]);
      if (boardRes.data?.success) setBoard(boardRes.data.data);
      if (empRes.data?.success) setEmployees(empRes.data.data || []);
    } catch (e) {
      toast.error('Plan yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [year, week]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Group routes by company → depot for stable rendering. Honour
  // filterCompanyIds, filterDepotIds, filterRouteIds and (when active)
  // filterFirmIds — if a firm filter is on, only routes whose `owner_firm_id`
  // matches one of the selected firms (or have no owner declared = legacy
  // /all-firm) remain. Depots without firm_ids show for all firms; with
  // firm_ids they must intersect the active firm set.
  const routeGroups = useMemo(() => {
    if (!board) return [];
    const depotById = new Map((board.depots || []).map((d) => [d.id, d]));
    const companyById = new Map((board.companies || []).map((c) => [c.id, c]));
    const byCompany = new Map();
    const showAllCompanies = !filterCompanyIds || filterCompanyIds.size === 0;
    const showAllDepots = !filterDepotIds || filterDepotIds.size === 0;
    const showAllRoutes = !filterRouteIds || filterRouteIds.size === 0;
    const firmFilterActive = filterFirmIds && filterFirmIds.size > 0;

    // Helper: is this company in scope of the active firm set?
    // - No firm filter active → always yes.
    // - Company has explicit firm_ids → must intersect the active firm set.
    // - Company has empty/missing firm_ids → legacy global, still visible.
    const companyInFirmScope = (companyId) => {
      if (!firmFilterActive) return true;
      const c = companyById.get(companyId);
      const ids = (c && Array.isArray(c.firm_ids)) ? c.firm_ids : [];
      if (ids.length === 0) return true;
      for (const fid of ids) if (filterFirmIds.has(fid)) return true;
      return false;
    };

    for (const r of board.routes) {
      if (!showAllCompanies && !filterCompanyIds.has(r.company_id)) continue;
      if (!showAllDepots && !(r.depot_id && filterDepotIds.has(r.depot_id))) continue;
      if (!showAllRoutes && !filterRouteIds.has(r.id)) continue;
      if (!companyInFirmScope(r.company_id)) continue;
      if (firmFilterActive) {
        // Skip routes owned by a firm not in the active set (when an owner is set).
        if (r.owner_firm_id && !filterFirmIds.has(r.owner_firm_id)) continue;
        // If the route has a depot, ensure that depot is in scope of one of the firms.
        if (r.depot_id) {
          const d = depotById.get(r.depot_id);
          if (d && Array.isArray(d.firm_ids) && d.firm_ids.length > 0) {
            let ok = false;
            for (const fid of d.firm_ids) if (filterFirmIds.has(fid)) { ok = true; break; }
            if (!ok) continue;
          }
        }
      }
      if (!byCompany.has(r.company_id)) {
        byCompany.set(r.company_id, { company_id: r.company_id, company_name: r.company_name, depots: new Map() });
      }
      const depotKey = r.depot_id || '__none__';
      const depots = byCompany.get(r.company_id).depots;
      if (!depots.has(depotKey)) {
        depots.set(depotKey, { depot_id: r.depot_id, depot_name: r.depot_name, routes: [] });
      }
      depots.get(depotKey).routes.push(r);
    }

    // When a firm filter is active, also surface depots that are in scope of
    // those firms even if they currently have no routes. This way "PostNL →
    // Oevel" still shows up under AYTU with a Yeni rota prompt instead of an
    // empty board. We only inject depots belonging to companies that the
    // customer-filter already permits (or all when no customer filter), and
    // — when a depot subfilter is on — only those explicitly selected.
    if (firmFilterActive && board.depots) {
      for (const d of board.depots) {
        if (!showAllCompanies && !filterCompanyIds.has(d.company_id)) continue;
        if (!showAllDepots && !filterDepotIds.has(d.id)) continue;
        if (!showAllRoutes) continue; // route filter implies "only listed routes"
        if (!companyInFirmScope(d.company_id)) continue;
        const firmIds = Array.isArray(d.firm_ids) ? d.firm_ids : [];
        if (firmIds.length > 0) {
          let ok = false;
          for (const fid of firmIds) if (filterFirmIds.has(fid)) { ok = true; break; }
          if (!ok) continue;
        }
        if (!byCompany.has(d.company_id)) {
          byCompany.set(d.company_id, { company_id: d.company_id, company_name: d.company_name, depots: new Map() });
        }
        const depots = byCompany.get(d.company_id).depots;
        if (!depots.has(d.id)) {
          depots.set(d.id, { depot_id: d.id, depot_name: d.name, routes: [] });
        }
      }
    }

    return [...byCompany.values()].map((c) => ({ ...c, depots: [...c.depots.values()] }));
  }, [board, filterCompanyIds, filterDepotIds, filterRouteIds, filterFirmIds]);

  // Index assignments by (route_id, date). Match either by worker firm OR
  // route owner firm so a borrowed Fast-Trans person on an Aytu route still
  // shows up when "Aytu" is part of the active firm set.
  const assignByCell = useMemo(() => {
    const m = new Map();
    if (!board) return m;
    const firmFilterActive = filterFirmIds && filterFirmIds.size > 0;
    for (const a of board.assignments) {
      if (firmFilterActive && !filterFirmIds.has(a.firm_id) && !filterFirmIds.has(a.owner_firm_id)) continue;
      m.set(`${a.route_id}|${a.assignment_date}`, a);
    }
    return m;
  }, [board, filterFirmIds]);

  const leaveByEmpDate = useMemo(() => {
    const s = new Set();
    if (!board) return s;
    for (const lb of board.leave_blocks) s.add(`${lb.user_id}|${lb.date}`);
    return s;
  }, [board]);

  // employee_id → Set(date) — used to grey contacts that would conflict.
  const employeeBookedDates = useMemo(() => {
    const m = new Map();
    if (!board) return m;
    for (const a of board.assignments) {
      if (!a.employee_id || a.status === 'cancelled') continue;
      if (!m.has(a.employee_id)) m.set(a.employee_id, new Set());
      m.get(a.employee_id).add(a.assignment_date);
    }
    return m;
  }, [board]);

  // employee_id → Set(date) where the employee has TWO OR MORE non-cancelled
  // assignments on that day (intentional double booking). The mini-bar paints
  // these segments red and the conflict panel uses the same data.
  const employeeDoubleBookedDates = useMemo(() => {
    const m = new Map();
    if (!board) return m;
    const counter = new Map(); // "empId|date" → count
    for (const a of board.assignments) {
      if (!a.employee_id || a.status === 'cancelled') continue;
      const k = `${a.employee_id}|${a.assignment_date}`;
      counter.set(k, (counter.get(k) || 0) + 1);
    }
    for (const [k, count] of counter) {
      if (count < 2) continue;
      const [empId, date] = k.split('|');
      if (!m.has(empId)) m.set(empId, new Set());
      m.get(empId).add(date);
    }
    return m;
  }, [board]);

  // Detailed conflict list for the side panel: every (employee, date) that has
  // ≥2 active assignments, with the actual assignments attached.
  const conflictList = useMemo(() => {
    if (!board) return [];
    const grouped = new Map(); // "empId|date" → [assignments]
    for (const a of board.assignments) {
      if (!a.employee_id || a.status === 'cancelled') continue;
      const k = `${a.employee_id}|${a.assignment_date}`;
      if (!grouped.has(k)) grouped.set(k, []);
      grouped.get(k).push(a);
    }
    const out = [];
    for (const [k, arr] of grouped) {
      if (arr.length < 2) continue;
      const [empId, date] = k.split('|');
      out.push({
        key: k,
        employee_id: empId,
        employee_name: arr[0].employee_name,
        firm_name: arr[0].firm_name,
        date,
        count: arr.length,
        assignments: arr,
      });
    }
    out.sort((a, b) => a.date.localeCompare(b.date) || a.employee_name.localeCompare(b.employee_name));
    return out;
  }, [board]);

  // Ordered weekly summary per employee (used by tile hover popover + mini-bar).
  // Each item: {date, type: 'leave'|'assigned', route_id, route_code, ...,
  //             doubleBooked: bool, doubleCount: int}
  const employeeWeekSummary = useMemo(() => {
    const m = new Map();
    if (!board) return m;
    // Pre-count conflicts so each item knows how many concurrent assignments
    // exist (drives mini-bar red highlight + tooltip).
    const dupCount = new Map(); // "empId|date" → count
    for (const a of board.assignments) {
      if (!a.employee_id || a.status === 'cancelled') continue;
      const k = `${a.employee_id}|${a.assignment_date}`;
      dupCount.set(k, (dupCount.get(k) || 0) + 1);
    }
    for (const lb of board.leave_blocks) {
      if (!m.has(lb.user_id)) m.set(lb.user_id, []);
      m.get(lb.user_id).push({ date: lb.date, type: 'leave' });
    }
    for (const a of board.assignments) {
      if (!a.employee_id || a.status === 'cancelled') continue;
      if (!m.has(a.employee_id)) m.set(a.employee_id, []);
      const dc = dupCount.get(`${a.employee_id}|${a.assignment_date}`) || 1;
      m.get(a.employee_id).push({
        date: a.assignment_date,
        type: 'assigned',
        route_id: a.route_id,
        route_code: a.route_code,
        company_name: a.company_name,
        shift_name: a.shift_name,
        doubleBooked: dc > 1,
        doubleCount: dc,
      });
    }
    for (const [k, arr] of m) {
      arr.sort((x, y) => x.date.localeCompare(y.date));
      m.set(k, arr);
    }
    return m;
  }, [board]);

  // Missing (empty) cells — respects the active firm/company filter and
  // EXCLUDES Sunday by default (operational reality: Sunday work is rare and
  // operators want to see how many *workday* slots are still open). Math:
  //   visible_routes × 6 weekdays − assignments-on-visible-routes-Mon..Sat
  const sundayDate = board?.week?.end_date || null; // ISO weeks → Sunday
  const visibleRouteIds = useMemo(() => {
    const ids = new Set();
    for (const c of routeGroups) {
      for (const dp of c.depots) {
        for (const r of dp.routes) ids.add(r.id);
      }
    }
    return ids;
  }, [routeGroups]);

  const assignedCount = useMemo(() => {
    if (!board) return 0;
    let n = 0;
    for (const key of assignByCell.keys()) {
      const [routeId, dateStr] = key.split('|');
      if (!visibleRouteIds.has(routeId)) continue;
      if (dateStr === sundayDate) continue;
      n += 1;
    }
    return n;
  }, [board, assignByCell, visibleRouteIds, sundayDate]);

  // Sunday assignments shown separately so the operator still sees them
  // when they exceptionally schedule weekend work.
  const sundayAssignedCount = useMemo(() => {
    if (!board || !sundayDate) return 0;
    let n = 0;
    for (const key of assignByCell.keys()) {
      const [routeId, dateStr] = key.split('|');
      if (!visibleRouteIds.has(routeId)) continue;
      if (dateStr === sundayDate) n += 1;
    }
    return n;
  }, [board, assignByCell, visibleRouteIds, sundayDate]);

  const missingCount = useMemo(() => {
    if (!board) return 0;
    // Mon-Fri counted always (5 days). Saturday counted ONLY when the toggle
    // is on AND the day isn't locked. Sunday is always excluded by convention.
    const dates = board.dates || [];
    const baseDays = 5;
    let saturdayCount = 0;
    if (showSaturday && dates.length >= 6) {
      const satDate = dates[5];
      if (!(lockedDates && lockedDates[satDate])) saturdayCount = 1;
    }
    let lockedWeekdays = 0;
    if (lockedDates) {
      for (let i = 0; i < Math.min(5, dates.length); i += 1) {
        if (lockedDates[dates[i]]) lockedWeekdays += 1;
      }
    }
    const totalCells = visibleRouteIds.size * (baseDays + saturdayCount - lockedWeekdays);
    return Math.max(0, totalCells - assignedCount);
  }, [board, visibleRouteIds, assignedCount, lockedDates, showSaturday]);

  // Conflict count — same (employee, day) appearing more than once.
  const conflictCount = useMemo(() => {
    if (!board) return 0;
    const seen = new Map();
    for (const a of board.assignments) {
      if (!a.employee_id || a.status === 'cancelled') continue;
      const k = `${a.employee_id}|${a.assignment_date}`;
      seen.set(k, (seen.get(k) || 0) + 1);
    }
    let n = 0;
    for (const v of seen.values()) if (v > 1) n += v;
    return n;
  }, [board]);

  return {
    board,
    employees,
    loading,
    routeGroups,
    assignByCell,
    leaveByEmpDate,
    employeeBookedDates,
    employeeDoubleBookedDates,
    employeeWeekSummary,
    conflictList,
    missingCount,
    assignedCount,
    sundayAssignedCount,
    conflictCount,
    fetchBoard,
  };
}
